import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close,
  Download,
  TrendingUp,
  Receipt,
  AccountBalance,
  Assessment,
} from '@mui/icons-material';
import dayjs from 'dayjs';

interface ReportViewerProps {
  open: boolean;
  onClose: () => void;
  reportType: string;
  reportData: any;
  startDate: string;
  endDate: string;
}

const ReportViewer: React.FC<ReportViewerProps> = ({
  open,
  onClose,
  reportType,
  reportData,
  startDate,
  endDate,
}) => {
  if (!reportData) return null;

  const getReportIcon = () => {
    switch (reportType) {
      case 'profit-loss':
        return <TrendingUp />;
      case 'gst':
        return <Receipt />;
      case 'cash-flow':
        return <AccountBalance />;
      case 'staff-performance':
        return <Assessment />;
      default:
        return <Assessment />;
    }
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'profit-loss':
        return 'Profit & Loss Report';
      case 'gst':
        return 'GST Report';
      case 'cash-flow':
        return 'Cash Flow Report';
      case 'staff-performance':
        return 'Staff Performance Report';
      default:
        return 'Report';
    }
  };

  const renderProfitLossReport = () => (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#10B98110' }}>
            <Typography variant="h6" color="#10B981" fontWeight={700}>
              ₹{reportData.revenue.totalRevenue.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#EF444410' }}>
            <Typography variant="h6" color="#EF4444" fontWeight={700}>
              ₹{reportData.expenses.totalExpenses.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Expenses
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#8B5CF610' }}>
            <Typography variant="h6" color="#8B5CF6" fontWeight={700}>
              ₹{reportData.netProfit.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Net Profit
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#06B6D410' }}>
            <Typography variant="h6" color="#06B6D4" fontWeight={700}>
              {reportData.profitMargin.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Profit Margin
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Revenue Breakdown */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Revenue Breakdown
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.revenueBreakdown.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="right">₹{item.amount.toLocaleString()}</TableCell>
                  <TableCell align="right">{item.percentage.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Expense Breakdown */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Expense Breakdown
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.expenseBreakdown.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="right">₹{item.amount.toLocaleString()}</TableCell>
                  <TableCell align="right">{item.percentage.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const renderGSTReport = () => (
    <Box>
      {/* GST Summary */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#10B98110' }}>
            <Typography variant="h6" color="#10B981" fontWeight={700}>
              ₹{reportData.sales.gstCollected.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              GST Collected
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#EF444410' }}>
            <Typography variant="h6" color="#EF4444" fontWeight={700}>
              ₹{reportData.purchases.gstPaid.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              GST Paid
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#8B5CF610' }}>
            <Typography variant="h6" color="#8B5CF6" fontWeight={700}>
              ₹{reportData.netGST.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Net GST
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* GST Transactions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          GST Transactions
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">GST Amount</TableCell>
                <TableCell align="right">GST Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.transactions.slice(0, 10).map((transaction: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{dayjs(transaction.date).format('MMM DD, YYYY')}</TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.type}
                      size="small"
                      color={transaction.type === 'SALE' ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{transaction.description}</TableCell>
                  <TableCell align="right">₹{transaction.amount.toLocaleString()}</TableCell>
                  <TableCell align="right">₹{transaction.gstAmount.toLocaleString()}</TableCell>
                  <TableCell align="right">{transaction.gstRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const renderCashFlowReport = () => (
    <Box>
      {/* Cash Flow Summary */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#10B98110' }}>
            <Typography variant="h6" color="#10B981" fontWeight={700}>
              ₹{reportData.openingBalance.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Opening Balance
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#06B6D410' }}>
            <Typography variant="h6" color="#06B6D4" fontWeight={700}>
              ₹{reportData.operatingActivities.netOperatingCash.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operating Cash Flow
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#8B5CF610' }}>
            <Typography variant="h6" color="#8B5CF6" fontWeight={700}>
              ₹{reportData.netCashFlow.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Net Cash Flow
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F9731610' }}>
            <Typography variant="h6" color="#F97316" fontWeight={700}>
              ₹{reportData.closingBalance.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Closing Balance
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Cash Flow Activities */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Cash Flow Activities
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Operating Activities
            </Typography>
            <Box mb={1}>
              <Typography variant="body2">Cash from Customers</Typography>
              <Typography variant="body1" fontWeight={600}>
                ₹{reportData.operatingActivities.cashFromCustomers.toLocaleString()}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2">Cash to Suppliers</Typography>
              <Typography variant="body1" fontWeight={600} color="error.main">
                -₹{reportData.operatingActivities.cashToSuppliers.toLocaleString()}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2">Cash to Employees</Typography>
              <Typography variant="body1" fontWeight={600} color="error.main">
                -₹{reportData.operatingActivities.cashToEmployees.toLocaleString()}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1" fontWeight={700}>
              Net: ₹{reportData.operatingActivities.netOperatingCash.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Investing Activities
            </Typography>
            <Box mb={1}>
              <Typography variant="body2">Equipment Purchases</Typography>
              <Typography variant="body1" fontWeight={600} color="error.main">
                -₹{reportData.investingActivities.equipmentPurchases.toLocaleString()}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2">Asset Sales</Typography>
              <Typography variant="body1" fontWeight={600}>
                ₹{reportData.investingActivities.assetSales.toLocaleString()}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1" fontWeight={700}>
              Net: ₹{reportData.investingActivities.netInvestingCash.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Financing Activities
            </Typography>
            <Box mb={1}>
              <Typography variant="body2">Owner Investments</Typography>
              <Typography variant="body1" fontWeight={600}>
                ₹{reportData.financingActivities.ownerInvestments.toLocaleString()}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2">Loan Proceeds</Typography>
              <Typography variant="body1" fontWeight={600}>
                ₹{reportData.financingActivities.loanProceeds.toLocaleString()}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2">Loan Repayments</Typography>
              <Typography variant="body1" fontWeight={600} color="error.main">
                -₹{reportData.financingActivities.loanRepayments.toLocaleString()}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1" fontWeight={700}>
              Net: ₹{reportData.financingActivities.netFinancingCash.toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  const renderStaffPerformanceReport = () => (
    <Box>
      {/* Top Performers */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Top Performers
        </Typography>
        <Grid container spacing={2}>
          {reportData.topPerformers.map((performer: any, index: number) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper sx={{ p: 2, bgcolor: '#E8B4B810' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {performer.metric}
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {performer.staffName}
                </Typography>
                <Typography variant="body1" color="#E8B4B8" fontWeight={600}>
                  {typeof performer.value === 'number' 
                    ? performer.value.toLocaleString() 
                    : performer.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Staff Performance Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Staff Performance Details
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff Name</TableCell>
                <TableCell align="right">Appointments</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Avg Service Value</TableCell>
                <TableCell align="right">Commission</TableCell>
                <TableCell align="right">Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.staffPerformance.map((staff: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{staff.staffName}</TableCell>
                  <TableCell align="right">{staff.totalAppointments}</TableCell>
                  <TableCell align="right">₹{staff.totalRevenue.toLocaleString()}</TableCell>
                  <TableCell align="right">₹{staff.averageServiceValue.toLocaleString()}</TableCell>
                  <TableCell align="right">₹{staff.commissionEarned.toLocaleString()}</TableCell>
                  <TableCell align="right">{staff.utilizationRate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const renderReportContent = () => {
    switch (reportType) {
      case 'profit-loss':
        return renderProfitLossReport();
      case 'gst':
        return renderGSTReport();
      case 'cash-flow':
        return renderCashFlowReport();
      case 'staff-performance':
        return renderStaffPerformanceReport();
      default:
        return <Typography>Report type not supported</Typography>;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: '#E8B4B8',
                color: 'white',
              }}
            >
              {getReportIcon()}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {getReportTitle()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dayjs(startDate).format('MMM DD, YYYY')} - {dayjs(endDate).format('MMM DD, YYYY')}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {renderReportContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          startIcon={<Download />}
          variant="outlined"
          sx={{
            borderColor: '#E8B4B8',
            color: '#E8B4B8',
            '&:hover': {
              borderColor: '#D4969B',
              bgcolor: 'rgba(232, 180, 184, 0.05)',
            },
          }}
        >
          Export Report
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportViewer;